import React, { useCallback, useMemo, useRef, useState } from 'react';

import debounce from 'lodash.debounce';
import useResizeObserver from 'use-resize-observer';

import type { TooltipStatefulProps } from '../../Tooltip';
import type { TextProps } from '../Text.types';

type UseOverflowProps = Pick<TextProps, 'maxVisibleLines' | 'overflowTooltipProps' | 'children'> & {
    elementRef: React.RefObject<HTMLElement>;
};

const DEF_SIZE = { width: 0, height: 0 };
const CALC_DEBOUNCE_TIMEOUT = 200;

const DEFAULT_TOOLTIP_PROPS: Partial<TooltipStatefulProps> = {
    direction: ['bottom', 'top'],
    size: 'm',
    hasTail: true,
    trimText: false,
};

type ResizeHandler = NonNullable<NonNullable<Parameters<typeof useResizeObserver>[0]>['onResize']>;

export const useOverflow = (props: UseOverflowProps) => {
    const { maxVisibleLines, overflowTooltipProps, children, elementRef } = props;

    const [hasTooltip, setHasTooltip] = useState(false);
    const refLastSize = useRef(DEF_SIZE);

    const calcHasTooltip = useCallback((): void => {
        const { width, height } = refLastSize.current;

        if (
            !elementRef.current ||
            !width ||
            overflowTooltipProps === null ||
            typeof maxVisibleLines !== 'number'
        ) {
            setHasTooltip(false);
            return;
        }

        const isHorizontalOverflow = elementRef.current.scrollWidth > width;
        const isVerticalOverflow = maxVisibleLines > 1 && elementRef.current.scrollHeight > height;

        setHasTooltip(isHorizontalOverflow || isVerticalOverflow);
    }, [elementRef, overflowTooltipProps, maxVisibleLines]);

    const debouncedCalc = useMemo(
        () => debounce(calcHasTooltip, CALC_DEBOUNCE_TIMEOUT),
        [calcHasTooltip],
    );

    const onResize: ResizeHandler = useCallback(
        (size) => {
            refLastSize.current = { width: size.width ?? 0, height: size.height ?? 0 };

            debouncedCalc();
        },
        [debouncedCalc],
    );

    useResizeObserver<HTMLElement>({
        ref: elementRef,
        onResize,
        box: 'border-box',
    });

    React.useEffect(() => {
        calcHasTooltip();
    }, [children, calcHasTooltip]);

    const tooltipProps = React.useMemo<Omit<TooltipStatefulProps, 'children'>>(
        () => ({
            contentRef: elementRef,
            content: children,
            ...DEFAULT_TOOLTIP_PROPS,
            ...overflowTooltipProps,
        }),
        [overflowTooltipProps, elementRef, children],
    );

    return { hasTooltip, tooltipProps };
};
